import { createSlice } from "@reduxjs/toolkit";
import {
  approveSingleLesson,
  cancelReservationGroupSingleAndPack,
  cancelReservationIndividualPack,
  cancelReservationIndividualSingle,
  getAllReservations,
  getEnrollmentsGroupPack,
  getEnrollmentsGroupSingle,
  getExpertSchedule,
  getGroupReservations,
  getIndividualReservations,
  getUserTotalPoints,
  refundIndividualPack,
  refundIndividualSingle,
  rejectReservationGroupSingleAndPack,
  rejectReservationIndividualPack,
  rejectReservationIndividualSingle,
  rescheduleIndividualPack,
  rescheduleIndividualSingle,
  sendLessonReoprtGroupPack,
  sendLessonReoprtGroupSingle,
  sendLessonReoprtIndividualSingle,
  studentReserveGroupPack,
  studentReserveIndividualPack,
  studentReserveIndividualSingle,
} from "redux/actions/reservations";
import { IIndividualReservation, TimeSlot } from "../models/reservations";

export const RESERVATION_TYPES = {
  INDIVIDUAL_LESSON_SINGLE: "INDIVIDUAL_LESSON_SINGLE",
  INDIVIDUAL_LESSON_PACK: "INDIVIDUAL_LESSON_PACK",
  GROUP_LESSON_SINGLE: "GROUP_LESSON_SINGLE",
  GROUP_LESSON_PACK: "GROUP_LESSON_PACK",
};

export enum RESERVATION_MODES {
  INITIAL_SCHEDULE = "INITIAL_SCHEDULE",
  RESCHEDULE = "RESCHEDULE",
}

export interface Reservations {
  inividualReservations: IIndividualReservation[];
  groupReservations: Array<any>;
  reservingLesson: any;
  scheduleCalanderOpen: boolean;
  loading: boolean;
  selectedExpertSchedule: Array<any>;
  selectedReservationTimeSlots: Array<TimeSlot>;
  finalTimeslots: Array<any>;
  reservationMode: RESERVATION_MODES;
}

const initialState: Reservations = {
  inividualReservations: [],
  groupReservations: [],
  reservingLesson: null,
  scheduleCalanderOpen: false,
  loading: false,
  selectedExpertSchedule: [],
  selectedReservationTimeSlots: [],
  finalTimeslots: [],
  reservationMode: RESERVATION_MODES.INITIAL_SCHEDULE,
};

export const reservationsSlice = createSlice({
  name: "reservationsSlice",
  initialState,
  reducers: {
    setReservingLesson(state, action) {
      state.reservingLesson = action.payload;
    },
    setScheduleCalanderOpen(state, action) {
      state.scheduleCalanderOpen = action.payload;
    },
    setSelectedReservationTimeSlots(state, action) {
      state.selectedReservationTimeSlots = action.payload;
    },
    setFinalTimeslots(state, action) {
      state.finalTimeslots = action.payload;
    },
    setReservationMode(state, action) {
      state.reservationMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // * get all reservations
    builder.addCase(getAllReservations.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getAllReservations.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getAllReservations.fulfilled, (state, action) => {
      state.loading = false;
      state.inividualReservations = action.payload?.individualReservations;
      state.groupReservations = action.payload?.groupReservations;
    });

    // * get reservations -individual
    builder.addCase(getIndividualReservations.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getIndividualReservations.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getIndividualReservations.fulfilled, (state, action) => {
      state.loading = false;
      state.inividualReservations = action.payload;
    });

    // * get reservations -group
    builder.addCase(getGroupReservations.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getGroupReservations.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getGroupReservations.fulfilled, (state, action) => {
      state.loading = false;
      state.groupReservations = action.payload;
    });

    // * approve reservations - individual
    builder.addCase(approveSingleLesson.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(approveSingleLesson.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(approveSingleLesson.fulfilled, (state, action) => {
      state.loading = false;
    });

    // * reject reservations - individual - single
    builder.addCase(
      rejectReservationIndividualSingle.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      rejectReservationIndividualSingle.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      rejectReservationIndividualSingle.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );
    // * reject reservations - individual - pack
    builder.addCase(
      rejectReservationIndividualPack.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      rejectReservationIndividualPack.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      rejectReservationIndividualPack.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );
    // * reject reservations - group - single and pack
    builder.addCase(
      rejectReservationGroupSingleAndPack.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      rejectReservationGroupSingleAndPack.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      rejectReservationGroupSingleAndPack.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );

    // * student reservation - group - pack & single
    builder.addCase(studentReserveGroupPack.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(studentReserveGroupPack.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(studentReserveGroupPack.fulfilled, (state, action) => {
      state.loading = false;
    });

    // * student reservation - individual - single
    builder.addCase(studentReserveIndividualSingle.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(
      studentReserveIndividualSingle.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      studentReserveIndividualSingle.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );
    // * student reservation - individual - pack
    builder.addCase(studentReserveIndividualPack.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(studentReserveIndividualPack.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(studentReserveIndividualPack.fulfilled, (state, action) => {
      state.loading = false;
    });

    // * get expert schedule
    builder.addCase(getExpertSchedule.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getExpertSchedule.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getExpertSchedule.fulfilled, (state, action) => {
      state.loading = false;
      state.selectedExpertSchedule = action.payload;
    });
    // * send lesson report - individual - single
    builder.addCase(
      sendLessonReoprtIndividualSingle.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      sendLessonReoprtIndividualSingle.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      sendLessonReoprtIndividualSingle.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );

    // * send lesson report - group - single
    builder.addCase(sendLessonReoprtGroupSingle.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(sendLessonReoprtGroupSingle.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(sendLessonReoprtGroupSingle.fulfilled, (state, action) => {
      state.loading = false;
    });
    // * send lesson report - group - pack
    builder.addCase(sendLessonReoprtGroupPack.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(sendLessonReoprtGroupPack.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(sendLessonReoprtGroupPack.fulfilled, (state, action) => {
      state.loading = false;
    });

    // * get enrollments - group - single
    builder.addCase(getEnrollmentsGroupSingle.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getEnrollmentsGroupSingle.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getEnrollmentsGroupSingle.fulfilled, (state, action) => {
      state.loading = false;
    });
    // * get enrollments - group - pack
    builder.addCase(getEnrollmentsGroupPack.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getEnrollmentsGroupPack.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getEnrollmentsGroupPack.fulfilled, (state, action) => {
      state.loading = false;
    });

    // * cancel reservations - group - pack and single
    builder.addCase(
      cancelReservationGroupSingleAndPack.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      cancelReservationGroupSingleAndPack.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      cancelReservationGroupSingleAndPack.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );

    // * cancel reservations - individual - single
    builder.addCase(
      cancelReservationIndividualSingle.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      cancelReservationIndividualSingle.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      cancelReservationIndividualSingle.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );
    // * cancel reservations - individual - pack
    builder.addCase(
      cancelReservationIndividualPack.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      cancelReservationIndividualPack.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      cancelReservationIndividualPack.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );
    // * reschedule reservations - individual - single
    builder.addCase(rescheduleIndividualSingle.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(rescheduleIndividualSingle.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(rescheduleIndividualSingle.fulfilled, (state, action) => {
      state.loading = false;
    });
    // * reschedule reservations - individual - pack
    builder.addCase(rescheduleIndividualPack.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(rescheduleIndividualPack.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(rescheduleIndividualPack.fulfilled, (state, action) => {
      state.loading = false;
    });
    // * refund reservations - individual - single
    builder.addCase(refundIndividualSingle.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(refundIndividualSingle.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(refundIndividualSingle.fulfilled, (state, action) => {
      state.loading = false;
    });
    // * refund reservations - individual - group
    builder.addCase(refundIndividualPack.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(refundIndividualPack.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(refundIndividualPack.fulfilled, (state, action) => {
      state.loading = false;
    });
  },
});

export const {
  setReservingLesson,
  setScheduleCalanderOpen,
  setSelectedReservationTimeSlots,
  setFinalTimeslots,
  setReservationMode,
} = reservationsSlice.actions;

export default reservationsSlice.reducer;
